import { eventQBus } from "@otto-ec/global-resources/event-q-bus";
import { createLogger } from "utils/logging";
import { ActivationClient } from "./ActivationClient";
import { ActivationStatus, buildDetailview, type Detailview } from "./Detailview";
import { ActivationTrigger } from "./events/ActivationTrigger";
import type { AddToBasketEvent } from "./events/AddToBasketEvent";
import type { TrackingService } from "./tracking/TrackingService";
import { attributeNameTriggerReload } from "./detailview_component";
import type { HTMLOcSnackbarV1Element } from "@otto-ec/otto-components/snackbar";
import { triggerHapticFeedback } from "@otto-ec/apps-api/hapticfeedback";
import { WishlistActivationBenefitType, type WishlistActivationEvent } from "./events/WishlistActivationEvent";
import { benefitEvents } from "@otto-ec/nexus-registry/benefit/events";
import { benefitWishlist } from "@otto-ec/nexus-registry/benefit/wishlist";
import { benefitSheet } from "@otto-ec/nexus-registry/benefit/sheet";

const log = createLogger("tag", "detailview.service");

/*                                                                                                    */
/*                                                                              */
const MAX_CLEANUP_PERIOD_IN_MILLIS = 20 * 24 * 60 * 60 * 1000;

/*                                                                                              */
const ACTIVATION_TIMEOUT_LOADER = 400;

export const wait = (timeout: number): Promise<void> => {
	return new Promise((resolve) => setTimeout(resolve, timeout));
};

export class DetailviewService {
	constructor(private readonly trackingService: TrackingService) {}

	private readonly activationClient = new ActivationClient();

	public initialize(variationId: string, customComponentElement: HTMLElement, eventMergeId: string | null) {
		const detailviewHtmlElement = customComponentElement.querySelector(".benefit-detailview__clean");
		if (!(detailviewHtmlElement && detailviewHtmlElement instanceof HTMLElement)) {
			return;
		}

		/*                                                                               */
		const sublineElement = detailviewHtmlElement.querySelector<HTMLElement>(".benefit-detailview__clean__action-container");
		if (!sublineElement) {
			log.error("Subline element not found");
			return;
		}

		const detailview = buildDetailview(detailviewHtmlElement, sublineElement, variationId);
		if (!detailview) {
			return;
		}

		this.handleAlreadyActivatedBenefit(detailview);
		initExpirationHandler(detailview, customComponentElement);

		if (detailview.activationStatus === ActivationStatus.DEACTIVATED || detailview.isEarlyAccess) {
			this.handleActivationFromOtherSources(detailview, customComponentElement);
		}

		log.info("Send load tracking event and set visible handler");
		this.trackingService.sendDetailviewBenefitLoadedEvent(detailview, eventMergeId);
		const toAppButtonElement = customComponentElement.querySelector<HTMLElement>("#benefit-detailview_app_btn");
		if (toAppButtonElement) {
			toAppButtonElement.addEventListener("click", () => {
				this.trackingService.sendDetailviewBenefitClickEvent(detailview);
			});
		}

		eventQBus.on("ft1.order-core.addToBasketResult", addToBasketHandler(this.trackingService, detailview).bind(this));

		/*                                   */
		const { activate } = window.otto.ft9.benefit.components.activationSlider;

		return activate.subscribe((event: any) => {
			if (!detailviewHtmlElement.contains(event.instance)) return;
			if (event.instance.dataset.benefitId !== detailview.benefitId) return;

			const activationUrl = event.instance.dataset.activationUrl;
			if (!activationUrl) {
				log.error("Missing activationUrl");
				return;
			}

			const errorLoggingPayload = {
				benefit_id: detailview.benefitId,
				benefit_active_until: detailview.activeUntil,
			};

			const startedMs = new Date().getMilliseconds();

			this.activationClient.activateViaUrl(activationUrl, errorLoggingPayload).then(async (success) => {
				const endedMs = new Date().getMilliseconds();

				/*                                                                                                                                                */
				const LOADING_STATE_TIMEOUT_MS = Math.max(0, ACTIVATION_TIMEOUT_LOADER + 200 - (endedMs - startedMs));

				const eventMergeId = this.trackingService.sendDetailviewBenefitSwipeEvent(detailview, success);

				if (success) {
					await wait(LOADING_STATE_TIMEOUT_MS);
					event.instance.setAttribute("is-activated", "true");
					detailview.activationStatus = ActivationStatus.ACTIVATED;
					setActivatedHeadline(detailviewHtmlElement);

					triggerHapticFeedback();
					sendActivationEventToPDPConsumer(detailview.benefitId, detailview.frameId, variationId, ActivationTrigger.ACTIVATED_BY_USER, true);
					sendActivationEventToWishlistConsumer(detailview.benefitId, detailview.frameId, true, eventMergeId);
				} else {
					if (window.o_util.toggle.get("ftnine_migrate_to_oc_snackbar", false)) {
						const snackbar = detailviewHtmlElement.querySelector<HTMLOcSnackbarV1Element>("#benefit-detailview_snackbar--error");
						if (snackbar) {
							snackbar.open = true;
						}
					} else {
						eventQBus.emit("assets.snackbar.trigger", {
							text: "Das hat leider nicht geklappt. Bitte probiere es noch einmal, danke.",
						});
					}

					await wait(LOADING_STATE_TIMEOUT_MS);
					event.instance.reset();
				}
			});
		});
	}

	private handleAlreadyActivatedBenefit(detailview: Detailview) {
		if (detailview.activationStatus === ActivationStatus.ACTIVATED) {
			sendActivationEventToPDPConsumer(detailview.benefitId, detailview.frameId, detailview.variationId, ActivationTrigger.FROM_DB_STORAGE, true);
		}
	}

	private handleActivationFromOtherSources(detailview: Detailview, detailviewHtml: HTMLElement) {
		/*                      */
		benefitSheet.active.subscribe(({ benefitId, reloadDelay }) => {
			if (!benefitId || (benefitId !== detailview.benefitId && benefitId !== detailview.frameId)) return;
			sendActivationEventToPDPConsumer(detailview.benefitId, detailview.frameId, detailview.variationId, ActivationTrigger.ACTIVATED_BY_USER, true);
			sendActivationEventToWishlistConsumer(detailview.benefitId, detailview.frameId, true);

			setTimeout(() => {
				detailviewHtml.setAttribute(attributeNameTriggerReload, `${new Date().getTime()}`);
			}, reloadDelay ?? 500); /*                                                                                       */
			/*                                                                                                           */
			/*                                                                                                             */
			/*               */
		});

		/*                                 */
		benefitEvents.active.subscribe(({ id, frameId, active, variationId, trigger }) => {
			if (!active || trigger !== ActivationTrigger.ACTIVATED_BY_USER) return;
			if (!variationId || variationId === detailview.variationId) return;

			const benefitId = id;

			const container = detailviewHtml.querySelectorAll<HTMLDivElement>(".benefit-detailview__clean__action-container");
			container.forEach((element) => {
				const benefitIdFromContainer = element.dataset.benefitId!;
				const frameIdFromContainer = element.dataset.frameId;

				if (benefitId === benefitIdFromContainer || frameIdFromContainer === frameId) {
					const activationSlider = element.querySelector("ft9-benefit-activation-slider-v1");
					if (activationSlider && !activationSlider.hasAttribute("is-activated")) {
						activationSlider.setAttribute("is-activated", "true");
						detailview.activationStatus = ActivationStatus.ACTIVATED;
						setActivatedHeadline(detailviewHtml);
					}
				}
			});
		});
	}
}

function initExpirationHandler(detailview: Detailview, detailviewHtml: HTMLElement) {
	const end: Date = new Date(detailview.activeUntil);
	const delayInMillis: number = Math.min(Math.max(end.getTime() - new Date().getTime() - 1000, 1), MAX_CLEANUP_PERIOD_IN_MILLIS);

	log.info(`Expiration delay: ${delayInMillis} ms`);

	window.setTimeout(() => {
		/*                                                                                */
		if (detailview.isEarlyAccess) {
			window.setTimeout(() => {
				detailviewHtml.setAttribute(attributeNameTriggerReload, `${new Date().getTime()}`);
			}, 1_000);
			return;
		}
		detailview.activationStatus = ActivationStatus.DEACTIVATED;
		sendActivationEventToPDPConsumer(detailview.benefitId, detailview.frameId, detailview.variationId, ActivationTrigger.EXPIRED_BENEFIT, false);
		sendActivationEventToWishlistConsumer(detailview.benefitId, detailview.frameId, false);

		window.setTimeout(() => {
			detailviewHtml.setAttribute(attributeNameTriggerReload, `${new Date().getTime()}`);
		}, 10_000);
	}, delayInMillis);
}

/*                                                            */
/*                                                                      */
/*                                                                      */
function sendActivationEventToPDPConsumer(benefitId: string, frameId: string | undefined, variationId: string, trigger: ActivationTrigger, active: boolean) {
	eventQBus.emit("ft9.benefit.active", {
		/*                                    */
		id: benefitId,
		frameId: frameId,
		active: active,
		variationId: variationId,
		trigger: trigger,
	});
	benefitEvents.active.emit({
		/*                                                   */
		id: benefitId,
		frameId: frameId,
		active: active,
		variationId: variationId,
		trigger: trigger,
	});
}

/*  */
/*                                                                         */
export function sendActivationEventToWishlistConsumer(benefitId: string, frameId: string | undefined, active: boolean, eventMergeId?: string) {
	let type: WishlistActivationBenefitType;
	if (frameId === undefined) {
		type = WishlistActivationBenefitType.PARTNER;
	} else {
		type = WishlistActivationBenefitType.PLATFORM;
	}

	benefitWishlist.active.emit({
		id: frameId || benefitId,
		type,
		active: active,
		eventMergeId,
	});
}

export function addToBasketHandler(trackingService: TrackingService, detailview: Detailview) {
	return (event: AddToBasketEvent) => {
		if (!event.variationId || !event.eventMergeId) {
			return;
		}
		if (event.variationId !== detailview.variationId) {
			/*                                                                               */
			/*                                                                                                 */
			return;
		}
		trackingService.sendDetailviewBenefitMergeEvent(detailview, event.eventMergeId);
	};
}

function setActivatedHeadline(detailviewHtmlElement: HTMLElement) {
	const headlineElement = detailviewHtmlElement.querySelector<HTMLElement>("[data-headline-activated]");
	if (!headlineElement) return;
	headlineElement.textContent = headlineElement.dataset.headlineActivated!;
}
